"use client";
import { useMotionValue, motion, useMotionTemplate } from "framer-motion";
import { cn } from "../../lib/utils";

export const CardSpotlight = ({
  children,
  radius = 250,
  color = "rgba(59, 130, 246, 0.15)",
  className,
  glow = true, // Enable glowing border by default
  ...props
}) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }) {
    let { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <div
      className={cn(
        "group/spotlight relative rounded-xl p-6 transition-all duration-300",
        glow
          ? "bg-card/30 backdrop-blur-sm border border-border/50 shadow-[0_0_15px_-3px_hsl(var(--primary)/0.1)] hover:shadow-[0_0_25px_-3px_hsl(var(--primary)/0.2)] hover:border-primary/30"
          : "border border-border bg-card",
        className
      )}
      onMouseMove={handleMouseMove}
      {...props}
    >
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-xl opacity-0 transition duration-300 group-hover/spotlight:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              ${radius}px circle at ${mouseX}px ${mouseY}px,
              ${color},
              transparent 80%
            )
          `,
        }}
      />
      {children}
    </div>
  );
};

// Alert card with spotlight effect
export const AlertCard = ({
  type = "warning",
  title,
  description,
  hint,
  count,
  items,
  actions,
  className,
}) => {
  const colors = {
    critical: {
      border: "border-red-500/30",
      bg: "bg-red-500/5",
      spotlight: "rgba(239, 68, 68, 0.15)",
      icon: "text-red-400",
      badge: "bg-red-500/20 text-red-400",
    },
    warning: {
      border: "border-amber-500/30",
      bg: "bg-amber-500/5",
      spotlight: "rgba(245, 158, 11, 0.15)",
      icon: "text-amber-400",
      badge: "bg-amber-500/20 text-amber-400",
    },
    info: {
      border: "border-blue-500/30",
      bg: "bg-blue-500/5",
      spotlight: "rgba(59, 130, 246, 0.15)",
      icon: "text-blue-400",
      badge: "bg-blue-500/20 text-blue-400",
    },
    success: {
      border: "border-emerald-500/30",
      bg: "bg-emerald-500/5",
      spotlight: "rgba(16, 185, 129, 0.15)",
      icon: "text-emerald-400",
      badge: "bg-emerald-500/20 text-emerald-400",
    },
  };

  const color = colors[type];

  return (
    <CardSpotlight
      color={color.spotlight}
      className={cn(color.border, color.bg, className)}
    >
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-foreground">{title}</h3>
              {count !== undefined && (
                <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", color.badge)}>
                  {count}
                </span>
              )}
            </div>
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
            {hint && (
              <p className="text-xs text-muted-foreground/80 mt-1 italic">{hint}</p>
            )}
          </div>
        </div>

        {items && items.length > 0 && (
          <div className="space-y-2 mb-4">
            {items.slice(0, 3).map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between text-sm p-2 rounded-lg bg-muted/50"
              >
                <span className="text-foreground/80 truncate max-w-[200px]">
                  {item.label}
                </span>
                {item.action && (
                  <button
                    onClick={item.action.onClick}
                    className="text-primary hover:text-primary/80 text-xs font-medium"
                  >
                    {item.action.label}
                  </button>
                )}
              </div>
            ))}
            {items.length > 3 && (
              <p className="text-xs text-muted-foreground">
                +{items.length - 3} more
              </p>
            )}
          </div>
        )}

        {actions && (
          <div className="flex items-center gap-2">
            {actions}
          </div>
        )}
      </div>
    </CardSpotlight>
  );
};
